import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePublicAuth } from '@/hooks/usePublicAuth';
import { CLIENTS } from '@/components/jurisprudence/MCPBadge';
import { LocaleSwitcher } from '@/components/LocaleSwitcher';
import { useTranslation } from 'react-i18next';
import { desktopBridge, isDesktop } from '@/AppMode';

interface MenuOverlayProps {
  open:    boolean;
  onClose: () => void;
  user:    ReturnType<typeof usePublicAuth>['user'];
  isPaid:  boolean;
}

export const MenuOverlay = ({ open, onClose, user, isPaid }: MenuOverlayProps) => {
  const { t } = useTranslation();

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // App desktop : pas d'encarts commerciaux ni de lien vers la page Desktop
  const desktop = isDesktop();

  return (
    <div
      className={`fixed inset-0 z-50 bg-background transition-opacity duration-250 ${
        open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Watermark */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <img
          src="/public/smartlawyer_animated.svg"
          alt=""
          aria-hidden="true"
          style={{
            position:   'absolute',
            right:      '-80px',
            bottom:     '-180px',
            width:      '900px',
            height:     '900px',
            opacity:    0.06,
            userSelect: 'none',
          }}
        />
      </div>

      {/* Barre du haut — croix seule */}
      <div className="relative z-10 flex items-center px-8 h-16">
        <button
          onClick={onClose}
          className="w-9 h-9 flex items-center justify-center text-xl text-muted-foreground hover:text-foreground transition-colors"
          aria-label={t('menu.close_menu')}
        >
          ✕
        </button>
      </div>

      {desktop ? (
        <DesktopMenuBody onClose={onClose} user={user} />
      ) : (
      <>
      {/* Corps : 2 colonnes */}
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1fr_380px] min-h-[calc(100vh-64px)] overflow-y-auto overflow-x-hidden">

        {/* ── Colonne gauche ── */}
        <div className="px-8 sm:px-12 py-6 lg:py-8 border-r border-border/40 flex flex-col">

          {/* Liens principaux */}
          <nav className="space-y-0 mb-0">
            {[
              { labelKey: 'menu.home',            to: '/' },
              { labelKey: 'menu.search_decision', to: '/browse' },
            ].map(({ labelKey, to }) => (
              <Link key={to} to={to} onClick={onClose}
                className="block font-serif text-[28px] font-bold text-primary leading-snug py-1.5 hover:pl-2 transition-all duration-150">
                › {t(labelKey)}
              </Link>
            ))}

            <a
              href="https://smartlawyer.ai/mcp/"
              onClick={onClose}
              className="flex items-center gap-2 font-serif text-[28px] font-bold text-primary leading-snug py-1.5 hover:pl-2 transition-all duration-150"
            >
              › {t('menu.connect_mcp')}
              <span className="inline-flex items-center gap-0.5 flex-shrink-0">
                {CLIENTS.map(c => (
                  <span key={c.id} title={c.name} className="opacity-60">
                    {c.iconHeader}
                  </span>
                ))}
              </span>
            </a>
          </nav>

          <div className="w-full h-px bg-border/60 my-5" />

          {/* Liens secondaires */}
          <nav className="space-y-0 mb-0">
            {[
              { labelKey: 'menu.faq',               to:'', href: 'https://smartlawyer.ai/faq/' },
              { labelKey: 'menu.explore_graph',     to: '/legal-graph' },
              ...(desktop ? [] : [{ labelKey: 'menu.desktop', to: '/desktop' }]),
              { labelKey: 'menu.about',             to: '/qui-sommes-nous' },
              { labelKey: 'nav.contact',            to: '/contact' },
            ].map(({ labelKey, to, href }) =>
              href ? (
                <a key={labelKey} href={href} onClick={onClose}
                  className="block text-[15px] font-medium text-foreground/80 hover:text-primary hover:pl-1.5 transition-all duration-150 py-1.5">
                  › {t(labelKey)}
                </a>
              ) : (
                <Link key={labelKey} to={to!} onClick={onClose}
                  className="block text-[15px] font-medium text-foreground/80 hover:text-primary hover:pl-1.5 transition-all duration-150 py-1.5">
                  › {t(labelKey)}
                </Link>
              )
            )}
          </nav>

          <div className="w-full h-px bg-border/60 my-5" />

          {/* Espace développeur */}
          <div>
            <div className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[.13em] text-muted-foreground bg-muted border border-border rounded-full px-2.5 py-1 mb-3">
              <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <polyline points="16 18 22 12 16 6"/>
                <polyline points="8 6 2 12 8 18"/>
              </svg>
              {t('menu.developer_space')}
            </div>
            <nav className="space-y-0">
              {[
                { labelKey: 'menu.documentation', href: 'https://smartlawyer.ai/documentation/' },
                { labelKey: 'menu.playground',    href: 'https://smartlawyer.ai/playground/' },
              ].map(({ labelKey, href }) => (
                <a key={labelKey} href={href} onClick={onClose}
                  className="block text-[15px] font-medium text-foreground/80 hover:text-primary hover:pl-1.5 transition-all duration-150 py-1.5">
                  › {t(labelKey)}
                </a>
              ))}
              <a
                href="https://smartlawyer.ai/docs/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
                className="inline-flex items-center gap-1.5 text-[15px] font-medium text-foreground/80 hover:text-primary hover:pl-1.5 transition-all duration-150 py-1.5"
              >
                › {t('menu.rest_api')}
                <svg width="11" height="11" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" className="opacity-40 flex-shrink-0">
                  <path d="M4 2H2a1 1 0 00-1 1v5a1 1 0 001 1h5a1 1 0 001-1V6M6 1h3m0 0v3m0-3L4.5 5.5"/>
                </svg>
              </a>
            </nav>
          </div>

          {/* CTAs mobile uniquement */}
          <div className="flex flex-col gap-3 lg:hidden mt-8">
            {!isPaid && !desktop && (
              <div className="rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/30 p-4 hover:bg-violet-100 dark:hover:bg-violet-950/50 hover:border-violet-300 transition-all">
                <p className="text-[9px] font-bold uppercase tracking-[.12em] text-violet-600 dark:text-violet-400 mb-1.5">
                  {t('menu.unlimited_access')}
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                  {t('menu.unlimited_features')}
                </p>
                <Link to="/pricing" onClick={onClose}
                  className="flex items-center justify-center w-full px-4 py-2.5 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition-colors">
                  {t('menu.subscribe_cta')}
                </Link>
              </div>
            )}
            {!user && (
              <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 p-4 hover:bg-blue-100 dark:hover:bg-blue-950/50 hover:border-blue-300 transition-all">
                <p className="text-[9px] font-bold uppercase tracking-[.12em] text-blue-600 dark:text-blue-400 mb-1.5">
                  {t('menu.free_account')}
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                  {t('menu.free_account_desc')}
                </p>
                <Link to="/login" onClick={onClose}
                  className="flex items-center justify-center w-full px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors">
                  {t('menu.create_account')}
                </Link>
              </div>
            )}
          </div>

          {/* Juridiction + langue (mobile / tablette — masqué dans le header sous md) */}
          <div className="mt-8 md:hidden">
            <LocaleSwitcher variant="inline" onChange={onClose} />
          </div>
        </div>

        {/* ── Colonne droite (desktop uniquement) ── */}
        <div className="hidden lg:flex flex-col bg-violet-50/60 dark:bg-violet-950/20 border-l border-border/40">
          <div className="flex flex-col gap-7 p-9 h-full">

            {/* Citation éditoriale */}
            <div>
              <p className="font-serif text-[17px] italic font-light text-foreground/80 leading-relaxed mb-2">
                {t('menu.quote_start')}{' '}
                <strong className="not-italic text-violet-600 dark:text-violet-400 font-bold">
                  {t('menu.quote_strong')}
                </strong>{' '}
                {t('menu.quote_end')}
              </p>
              <p className="text-[11px] text-muted-foreground">— SmartLawyer</p>
            </div>

            {/* Encart S'abonner (non-paid uniquement) */}
            {!isPaid && !desktop && (
              <Link
                to="/pricing"
                onClick={onClose}
                className="group rounded-xl border border-violet-200 dark:border-violet-800 bg-white dark:bg-background p-4 hover:border-violet-400 dark:hover:border-violet-600 hover:shadow-md hover:shadow-violet-100 dark:hover:shadow-violet-950/30 transition-all"
              >
                <p className="text-[9px] font-bold uppercase tracking-[.12em] text-violet-600 dark:text-violet-400 mb-1.5">
                  {t('menu.unlimited_access')}
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  {t('menu.unlimited_features')}
                </p>
                <span className="flex items-center justify-center w-full px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold group-hover:bg-violet-700 transition-colors">
                  {t('menu.subscribe_cta')}
                </span>
              </Link>
            )}

            {/* Encart Compte gratuit (non connecté) */}
            {!user && (
              <Link
                to="/login"
                onClick={onClose}
                className="group rounded-xl border border-blue-200 dark:border-blue-800 bg-white dark:bg-background p-4 hover:border-blue-400 dark:hover:border-blue-600 hover:shadow-md hover:shadow-blue-100 dark:hover:shadow-blue-950/30 transition-all"
              >
                <p className="text-[9px] font-bold uppercase tracking-[.12em] text-blue-600 dark:text-blue-400 mb-1.5">
                  {t('menu.free_account')}
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  {t('menu.free_account_desc')}
                </p>
                <span className="flex items-center justify-center w-full px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold group-hover:bg-blue-700 transition-colors">
                  {t('menu.create_account')}
                </span>
              </Link>
            )}

            {/* Connecté : carte utilisateur */}
            {user && (
              <div className="rounded-xl border bg-white dark:bg-background p-4 mt-auto">
                <div className="flex items-center gap-3">
                  {user.photoURL ? (
                    <img src={user.photoURL} alt={t('header.avatar_alt')}
                      className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-primary/20 text-primary flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {(user.displayName || user.email || 'U')[0].toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">
                      {user.displayName || user.email}
                    </p>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                        isPaid
                          ? 'bg-primary/10 text-primary'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {isPaid ? t('header.plan_pro') : t('common.free')}
                      </span>
                      {!isPaid && !desktop && (
                        <span className="text-[10px] text-muted-foreground">{t('menu.fiches_per_day')}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
};
// App desktop : l'outil seulement — recherche, corpus, espace local, compte.
// Pas de pages du site (FAQ, à propos, MCP, documentation…).
const DESKTOP_LINKS = [
  { to: '/', label: 'menu.home', icon: '🔎' },
  { to: '/articles', label: 'Codes & lois', icon: '📘' },
  { to: '/jurisprudence/cjue', label: 'CJUE', icon: '🇪🇺' },
  { to: '/conseil-constitutionnel', label: 'Conseil constitutionnel', icon: '🏛️' },
  { to: '/cnil/deliberations', label: 'CNIL', icon: '🔐' },
  { to: '/legal-graph', label: 'Legal Graph', icon: '🕸️' },
  { to: '/tribunal', label: 'Tribunal', icon: '🔨' }
];

const LOCAL_LINKS = [
  { view: 'dossiers', label: 'desktop.home.my_files', icon: '📁' },
  { view: 'modeles', label: 'desktop.menu.models', icon: '🧠' },
  { view: 'reglages', label: 'desktop.menu.settings', icon: '⚙️' }
];

function DesktopMenuBody({
  onClose,
  user
}: {
  onClose: () => void;
  user: MenuOverlayProps['user'];
}) {
  const { t } = useTranslation();
  const bridge = desktopBridge();
  const linkCls =
    'flex items-center gap-3 text-[17px] font-medium text-foreground/85 hover:text-primary hover:pl-1.5 transition-all duration-150 py-2';
  return (
    <div className="relative z-10 max-w-xl px-8 sm:px-12 py-6 overflow-y-auto max-h-[calc(100vh-64px)]">
      <nav>
        {DESKTOP_LINKS.map((l) => (
          <Link key={l.to} to={l.to} onClick={onClose} className={linkCls}>
            <span className="w-6 text-center">{l.icon}</span>
            {/* noms propres des corpus : non traduits (comme sur DesktopHome) */}
            {l.label.startsWith('menu.') ? t(l.label) : l.label}
          </Link>
        ))}
      </nav>
      {bridge && (
        <>
          <div className="w-full h-px bg-border/60 my-5" />
          <p className="text-[10px] font-bold uppercase tracking-[.13em] text-muted-foreground mb-2">
            {t('desktop.menu.local_space')}
          </p>
          <nav>
            {LOCAL_LINKS.map((l) => (
              <button
                key={l.view}
                onClick={() => {
                  onClose();
                  bridge.app.openLocal?.(l.view);
                }}
                className={linkCls + ' w-full text-left'}
              >
                <span className="w-6 text-center">{l.icon}</span>
                {t(l.label)}
              </button>
            ))}
          </nav>
        </>
      )}
      <div className="w-full h-px bg-border/60 my-5" />
      {user ? (
        <p className="text-sm text-muted-foreground truncate">
          {user.displayName || user.email}
        </p>
      ) : (
        <Link to="/login" onClick={onClose} className={linkCls}>
          <span className="w-6 text-center">👤</span>
          {t('common.login')}
        </Link>
      )}
      <div className="mt-6">
        <LocaleSwitcher variant="inline" onChange={onClose} />
      </div>
    </div>
  );
}
